import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Header } from "@/components/layouts/Header";
import { useUser, useSupabaseClient } from "@supabase/auth-helpers-react";
import { Spinner } from "@/components/ui/spinner";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { cn } from "@/lib/utils";
import BottomNavigation from "../ui/BottomNavigation";

interface MainLayoutProps {
  children: React.ReactNode;
  className?: string;
  isProfile?: boolean;
}

export const MainLayout = ({
  children,
  className = "",
  isProfile = false,
}: MainLayoutProps) => {
  const navigate = useNavigate();
  const user = useUser();
  const supabase = useSupabaseClient();
  const { isOnboarded, isLoading } = useOnboardingStatus(user?.id);
  // Track if we're in the process of logging out
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Set up auth state change listener
  useEffect(() => {
    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // If the event is SIGNED_OUT, mark that we're logging out
      if (event === "SIGNED_OUT") {
        setIsLoggingOut(true);
      } else if (event === "SIGNED_IN") {
        setIsLoggingOut(false);
      }
    });

    // Clean up the subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  // Handle onboarding redirect
  useEffect(() => {
    // Only redirect to onboarding if:
    // 1. User is loaded
    // 2. User is not onboarded
    // 3. We're not in the process of logging out
    if (user && !isLoading && !isOnboarded && !isLoggingOut) {
      navigate("/onboarding");
    }
  }, [user, isOnboarded, isLoading, navigate, isLoggingOut]);

  // Show loading state while checking onboarding status or waiting for user data
  if ((isLoading && user) || !user) {
    return <div></div>;
  }

  return (
    <div
      className={cn("min-h-screen", {
        "bg-background": !isProfile,
        "bg-gray-100 dark:bg-black": isProfile,
      })}
    >
      <Header />
      <main
        className={`container px-0 mx-auto pb-16 md:pb-0 lg:px-4 py-4 max-w-none lg:max-w-screen-xl ${className}`}
      >
        {children}
      </main>
      <BottomNavigation />
    </div>
  );
};
